import React from 'react'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Navigation} from 'swiper/modules'
import {HStack, Box, Flex, Text, useMultiStyleConfig, StackProps, TextProps} from '@chakra-ui/react'
import {Icon} from '_uiComponents'

interface SlidesToViewProps {
    base?: number | 'auto'
    sm?: number | 'auto'
    md?: number | 'auto'
    lg: number | 'auto'
}

interface ProductsSwiperProps {
    title?: React.ReactNode
    slidesToView: SlidesToViewProps
    children: React.ReactNode
    isLoop?: boolean
}

const ProductsSwiper = ({title, slidesToView, isLoop = true, children}: ProductsSwiperProps) => {
    const styles = useMultiStyleConfig('ProductsSwiper') as Record<string, StackProps | TextProps>

    const slidesToViewMdChecker = slidesToView.md !== undefined && slidesToView.md !== 'auto' ? slidesToView.md : undefined
    const slidesToViewLgChecker = slidesToView.lg !== undefined && slidesToView.lg !== 'auto' ? slidesToView.lg : undefined
    const shouldSeeArrows =
        (slidesToViewMdChecker && React.Children.count(children) > slidesToViewMdChecker) ||
        (slidesToViewLgChecker && React.Children.count(children) > slidesToViewLgChecker)
    const randomId = Math.floor(Math.random() * 100) + 1

    return (
        <Box {...styles.wrapper}>
            {title && (
                <Box {...styles.titleWrapper}>
                    <Text as="h2" {...(styles.title as TextProps)}>
                        {title}
                    </Text>
                </Box>
            )}
            {shouldSeeArrows && (
                <HStack {...styles.navigation}>
                    <Flex
                        {...styles.navigationPrev}
                        className={`swiper-button-prev swiper-button-prev-${randomId}`}
                        cursor="pointer"
                        data-testid="swiper-button-prev"
                    >
                        <Icon name="IconChevronLeft" size="sm" />
                    </Flex>
                    <Flex
                        {...styles.navigationNext}
                        className={`swiper-button-next swiper-button-next-${randomId}`}
                        cursor="pointer"
                        data-testid="swiper-button-next"
                    >
                        <Icon name="IconChevronRight" size="sm" />
                    </Flex>
                </HStack>
            )}

            <Box {...styles.swiper}>
                <Swiper
                    modules={[Navigation]}
                    navigation={
                        shouldSeeArrows
                            ? {
                                  prevEl: `.swiper-button-prev-${randomId}`,
                                  nextEl: `.swiper-button-next-${randomId}`,
                              }
                            : false
                    }
                    spaceBetween={24}
                    slidesPerView={slidesToView.lg}
                    allowTouchMove={true}
                    loop={isLoop}
                    speed={600}
                    breakpoints={{
                        1: {
                            slidesPerView: slidesToView.base ?? 1,
                        },
                        519: {
                            slidesPerView: slidesToView.sm ?? slidesToView.base ?? 2,
                        },
                        768: {
                            slidesPerView: slidesToView.md ?? slidesToView.sm ?? 2,
                        },
                        1024: {
                            slidesPerView: slidesToView.lg,
                        },
                    }}
                >
                    {React.Children.map(children, (child, index) => (
                        <>
                            <SwiperSlide key={index} style={{height: 'auto'}}>
                                {child}
                            </SwiperSlide>
                        </>
                    ))}
                </Swiper>
            </Box>
        </Box>
    )
}

export {ProductsSwiper}
